import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  useTheme,
  useMediaQuery,
  IconButton
} from '@mui/material';
import { PopularityItem, PopularityItemType } from '../../types';
import { colors } from '../../theme/themeConfig';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import StarIcon from '@mui/icons-material/Star';

interface PopularityItemCardProps {
  item: PopularityItem;
  quantity: number;
  imageSrc: string;
  bonusPoints: number;
  onAdd: () => void;
  onRemove: () => void;
}

const PopularityItemCard: React.FC<PopularityItemCardProps> = ({
  item,
  quantity,
  imageSrc,
  bonusPoints,
  onAdd,
  onRemove,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colors.background.card,
        borderRadius: theme.shape.borderRadius,
        boxShadow: theme.shadows[2],
        border: `1px solid ${colors.primary.main}`,
        padding: theme.spacing(1.5),
        marginBottom: theme.spacing(1),
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
      }}
    >
      {/* Image Section */}
      <Box
        sx={{
          width: '100%',
          height: isMobile ? 80 : 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: theme.spacing(1),
        }}
      >
        <img
          src={imageSrc}
          alt={item.name}
          loading="lazy"
          decoding="async"
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
          }}
        />
      </Box>

      {/* Amount + Bonus Row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: theme.spacing(0.75),
        }}
      >
        {item.itemType === PopularityItemType.SPECIAL_ITEM ? (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontSize: theme.typography.pxToRem(16),
                fontWeight: 600,
                color: colors.text.primary,
                margin: 0,
              }}
            >
              {item.name}
            </Typography>
            <Typography
              sx={{
                fontSize: theme.typography.pxToRem(12),
                color: colors.text.secondary,
                margin: 0,
              }}
            >
              {item.ppAmount.toLocaleString()} PP
            </Typography>
          </Box>
        ) : (
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: theme.typography.pxToRem(18),
              fontWeight: 500,
              color: colors.text.primary,
              margin: 0,
              whiteSpace: 'nowrap',
            }}
          >
            {item.ppAmount.toLocaleString()} PP
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            component="span"
            sx={{
              color: colors.secondary.main,
              fontWeight: 500,
              fontSize: theme.typography.pxToRem(14),
              marginRight: theme.spacing(0.25),
            }}
          >
            {bonusPoints}
          </Typography>
          <StarIcon
            sx={{
              color: colors.secondary.main,
              fontSize: theme.typography.pxToRem(16)
            }}
          />
        </Box>
      </Box>

      {/* Price */}
      <CardContent
        sx={{
          flex: 1,
          padding: 0,
          marginTop: theme.spacing(-0.5),
          marginBottom: theme.spacing(0.75),
          '&:last-child': { paddingBottom: 0 },
        }}
      >
        <Typography
          variant="body1"
          sx={{
            fontSize: theme.typography.pxToRem(14),
            fontWeight: 500,
            marginBottom: 0,
            color: colors.text.primary,
          }}
        >
          {item.price} рублей
        </Typography>
      </CardContent>

      {/* Button / Counter */}
      <CardActions sx={{ padding: 0 }}>
        {quantity === 0 ? (
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={onAdd}
            sx={{
              borderRadius: (theme.shape.borderRadius as number) / 2,
              padding: theme.spacing(1, 0),
              fontSize: theme.typography.pxToRem(14),
              fontWeight: 500,
              textTransform: 'none',
              transition: 'background-color 0.2s ease-in-out',
            }}
          >
            Выбрать
          </Button>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              border: `1px solid ${colors.primary.main}`,
              borderRadius: (theme.shape.borderRadius as number) / 2,
              padding: theme.spacing(0.6),
            }}
          >
            <IconButton
              onClick={onRemove}
              size="small"
              sx={{
                color: colors.primary.main,
                transition: 'color 0.2s ease-in-out',
                '&:hover': {
                  color: colors.primary.dark,
                }
              }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>

            <Typography
              sx={{
                fontSize: theme.typography.pxToRem(14),
                fontWeight: 500,
                color: colors.text.primary,
              }}
            >
              {quantity}
            </Typography>

            <IconButton
              onClick={onAdd}
              size="small"
              sx={{
                color: colors.primary.main,
                transition: 'color 0.2s ease-in-out',
                '&:hover': {
                  color: colors.primary.dark,
                }
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </CardActions>

      {/* Disclaimer */}
      <Typography
        variant="caption"
        align="center"
        sx={{
          fontSize: theme.typography.pxToRem(9),
          color: colors.text.secondary,
          marginTop: theme.spacing(0.5),
        }}
      >
        Вы получите бонусы при оплате через СБП
      </Typography>
    </Card>
  );
};

export default PopularityItemCard;
